"use client";

import React, { useState } from "react";
import { 
  X, 
  Stethoscope, 
  CheckCircle2, 
  Clock, 
  Sparkles, 
  Calendar, 
  ShieldCheck, 
  AlertCircle 
} from "lucide-react";
import { 
  DENTAL_TREATMENTS, 
  DENTAL_DOCTORS, 
  CONSULTATION_TYPES, 
  CLINIC_TIME_SLOTS 
} from "@/lib/demos/dentalData";

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency?: "INR" | "USD";
  preselectedTreatment?: string;
}

export default function ConsultationModal({
  isOpen,
  onClose,
  currency = "INR",
  preselectedTreatment,
}: ConsultationModalProps) {
  const defaultTreatment = preselectedTreatment || DENTAL_TREATMENTS[0].name;

  const [formData, setFormData] = useState({
    patient_name: "",
    email: "",
    phone: "",
    treatment_interest: defaultTreatment,
    consultation_type: CONSULTATION_TYPES[0],
    preferred_doctor: DENTAL_DOCTORS[0].name + " (" + DENTAL_DOCTORS[0].title.split("&")[0].trim() + ")",
    preferred_slot: CLINIC_TIME_SLOTS[0],
    clinical_notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/demos/dental/patients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          currency,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || "Failed to submit clinical consultation.");
      }
    } catch (err: any) {
      setError(err.message || "Network error submitting consultation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-slate-950 border border-slate-800 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative shadow-2xl overflow-y-auto max-h-[90vh] text-slate-100">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-8 space-y-4 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mx-auto text-teal-400">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-[11px] font-bold text-teal-400 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Consultation Registered</span>
            </div>

            <h3 className="text-2xl font-black text-white tracking-tight">
              Clinical Intake Confirmed
            </h3>

            <p className="text-xs text-slate-300 max-w-md mx-auto leading-relaxed">
              Thank you, <strong>{formData.patient_name}</strong>. An orientation confirmation has been dispatched to <strong>{formData.email}</strong>.
            </p>

            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 text-left text-xs space-y-2 max-w-md mx-auto text-slate-300">
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">Treatment Focus:</span>
                <span className="font-semibold text-white truncate max-w-[240px]">{formData.treatment_interest}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">Attending Specialist:</span>
                <span className="font-semibold text-teal-400">{formData.preferred_doctor.split(" (")[0]}</span>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-slate-400">Diagnostic Suite:</span>
                <span className="font-semibold text-emerald-400">iTero Lumina 3D Scan Operatory</span>
              </div>
            </div>

            <div className="pt-4 flex justify-center">
              <button
                onClick={onClose}
                className="py-2.5 px-8 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-extrabold text-xs transition-colors shadow-lg shadow-teal-500/20"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400">
                  <Stethoscope className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-black text-white">
                    Book Clinical 3D Consultation
                  </h2>
                  <p className="text-xs text-slate-400">
                    Includes photorealistic iTero 3D intraoral optical scan and specialist examination.
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Patient Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dr. Siddharth Sen"
                  value={formData.patient_name}
                  onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Email (For Clinical Pass PDF) *
                </label>
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Mobile / WhatsApp (For Reminders)
                </label>
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Preferred Specialist Doctor
                </label>
                <select
                  value={formData.preferred_doctor}
                  onChange={(e) => setFormData({ ...formData, preferred_doctor: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-teal-500"
                >
                  {DENTAL_DOCTORS.map((doc) => (
                    <option key={doc.id} value={`${doc.name} (${doc.title.split("&")[0].trim()})`}>
                      {doc.name} — {doc.title.split("&")[0].trim()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Treatment Focus *
                </label>
                <select
                  value={formData.treatment_interest}
                  onChange={(e) => setFormData({ ...formData, treatment_interest: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-teal-500"
                >
                  {DENTAL_TREATMENTS.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Consultation Nature
                </label>
                <select
                  value={formData.consultation_type}
                  onChange={(e) => setFormData({ ...formData, consultation_type: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-teal-500"
                >
                  {CONSULTATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Preferred Appointment Timing
                </label>
                <select
                  value={formData.preferred_slot}
                  onChange={(e) => setFormData({ ...formData, preferred_slot: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-teal-500"
                >
                  {CLINIC_TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                  Tooth Concerns, Medical History or Previous Treatments (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Diastema between front central incisors, mild dental anxiety, looking for non-invasive veneer trial."
                  value={formData.clinical_notes}
                  onChange={(e) => setFormData({ ...formData, clinical_notes: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>

            <div className="p-3 rounded-xl bg-teal-500/5 border border-teal-500/20 text-[11px] text-teal-300/90 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-teal-400 shrink-0" />
              <span>100% Class-B medical autoclave sterilization. All operatory tools sterile-sealed and unboxed chairside.</span>
            </div>

            <div className="pt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-slate-950 font-bold text-xs transition-all shadow-md shadow-teal-500/20 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? "Booking Consultation..." : "Book Clinical Consultation"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
